/**
 * Feature flags as plain data — deliberately zod-free.
 *
 * These constants are read on every app boot: the web console's branding store
 * seeds itself from `FEATURE_DEFAULTS` before the first render, and the sidebar
 * gates its nav items on `FeatureKey`. When they lived in `settings.schema.ts`
 * that boot path had to import a zod module, which pulled zod (~146 KB of
 * source) and the whole 46 KB settings schema into the entry chunk — for one
 * object of booleans that needs neither.
 *
 * `settings.schema.ts` re-exports everything here, so `FeatureFlagsSchema` and
 * friends stay the validation source of truth and no import site had to change.
 * A compile-time assertion there keeps the schema and `FeatureFlags` in step.
 */

// ---------------------------------------------------------------------------
// Feature flags — toggle whole modules on/off so one codebase serves both a
// lean "workers attendance" deployment and the full HR suite. Core attendance
// (check-in/out, workers, sites & camps, live + manpower boards, notifications,
// profile, settings, system) is always on and has no flag. Every flag DEFAULTS
// TRUE so existing installs keep all features until an admin turns one off.
// ---------------------------------------------------------------------------

export const FEATURE_KEYS = [
  'leave',
  'corrections',
  'announcements',
  'letters',
  'payroll',
  'reports',
  'shifts',
  'team',
  'orgStructure',
  'documents',
  'faceRecognition',
  'gratuity',
  'lifecycle',
  'dailyRoster',
  'selfService',
  'workers',
  'camps',
  'hardwareTerminals',
] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];

/**
 * The full flag set. Structurally identical to `z.infer<typeof
 * FeatureFlagsSchema>` — `settings.schema.ts` asserts that at compile time, so
 * adding a flag to one and not the other fails typecheck rather than drifting.
 */
export type FeatureFlags = Record<FeatureKey, boolean>;

/**
 * Every flag on. Derived from `FEATURE_KEYS` rather than written out, so a new
 * flag cannot be forgotten here.
 *
 * This used to be `FeatureFlagsSchema.parse({})`, which ran a zod parse during
 * module evaluation on every page load to rediscover a constant.
 */
export const FEATURE_DEFAULTS: FeatureFlags = Object.fromEntries(
  FEATURE_KEYS.map((key) => [key, true])
) as FeatureFlags;

/**
 * Lean "workers attendance" preset — keeps the attendance core (check-in/out,
 * corrections, reports/boards, team visibility, face enrolment) and turns the
 * HR-heavy modules off. Surfaced as a one-click preset in Settings → Features.
 */
export const LEAN_ATTENDANCE_FEATURES: FeatureFlags = {
  leave: false,
  corrections: true,
  announcements: false,
  letters: false,
  payroll: false,
  reports: true,
  shifts: false,
  team: true,
  orgStructure: false,
  documents: false,
  faceRecognition: true,
  gratuity: false,
  lifecycle: false,
  dailyRoster: true,
  selfService: true,
  workers: true,
  // The lean preset is the SITE-attendance deployment, but it must not silently
  // switch a pillar off: an existing tenant that applies the preset keeps its
  // camps. Turning camps off is a deliberate, separate decision (it hides camp
  // history, though it never changes a recorded figure).
  camps: true,
  // Standalone ZKTeco/Hikvision face terminals are attendance-CAPTURE
  // infrastructure, same bucket as faceRecognition above — not an HR add-on
  // the lean deployment would want stripped.
  hardwareTerminals: true,
};

/**
 * Office / HR-only preset — the mirror image of `LEAN_ATTENDANCE_FEATURES`.
 * For a tenant with no site workers at all: everyone signs in, punches from
 * the web or their own phone, and HR runs the full suite around them. Turns
 * off the whole site-workforce pillar (`workers`) and everything that exists
 * only to serve it (`camps`, `dailyRoster`, `faceRecognition`) rather than
 * leaving them on and merely hidden — `workers=false` already hides every
 * one of these from the nav and soft-hides their data (never deletes it), so
 * switching back on later loses nothing.
 *
 * `hardwareTerminals` defaults off too: a standalone ZKTeco/Hikvision
 * terminal is capture infrastructure for a physical gate or yard, which an
 * office-only deployment does not have by definition. An admin who does run
 * a badge terminal at a single office door can switch it back on — this is
 * a starting point, not a lock.
 */
export const OFFICE_ONLY_FEATURES: FeatureFlags = {
  leave: true,
  corrections: true,
  announcements: true,
  letters: true,
  payroll: true,
  reports: true,
  shifts: true,
  team: true,
  orgStructure: true,
  documents: true,
  faceRecognition: false,
  gratuity: true,
  lifecycle: true,
  dailyRoster: false,
  selfService: true,
  workers: false,
  camps: false,
  hardwareTerminals: false,
};

/**
 * Terminal-only preset — a gate, a clock, and nothing else.
 *
 * For the deployment that has one job: a hardware terminal at the door records
 * who came in and who left, and somebody reads the log. Nobody signs into
 * Hadir except the administrator. There is no leave to request, no payslip to
 * issue, no manager to approve anything, and no field app.
 *
 * Neither existing preset fits it. `LEAN_ATTENDANCE_FEATURES` is the
 * construction deployment — camps, daily roster, self-service, corrections.
 * `OFFICE_ONLY_FEATURES` turns the entire HR suite ON, which is the opposite of
 * what this tenant asked for, and turns hardware terminals OFF.
 *
 * ── WHY `workers` STAYS ON, WHEN THE TENANT SAYS THEY HAVE NONE ─────────────
 * The flag is named for the site-workforce pillar, but two things this
 * deployment cannot do without live inside it:
 *
 *   LOGIN-LESS PEOPLE. `workers: false` removes the Labour employee type, and
 *     Labour is Hadir's shape for a person with no user account. This tenant's
 *     entire workforce is that shape — the terminal recognises them, they never
 *     sign in.
 *   A PLACE FOR THE TERMINAL. Every terminal is registered against a worksite
 *     (`registerTerminal` requires one), and the Worksites screen is gated on
 *     this flag. With it off, the one screen needed to finish setup is missing
 *     from the navigation.
 *
 * What the tenant means by "no workers" — no camps, no daily roster, no field
 * app, no site-labour machinery — is switched off individually below. The
 * pillar itself is load-bearing here.
 *
 * ── `corrections` IS OFF, AND WHAT THAT DOES *NOT* COST ─────────────────────
 * That module is the REQUEST-and-approve queue, and `POST /api/corrections`
 * raises against `requireCallerEmployeeId` — the CALLER's own record. With
 * nobody signing in there is nobody to raise one, and an administrator cannot
 * use it on somebody else's behalf anyway. It would be a permanently empty
 * screen, so it is off.
 *
 * Reviewing a FLAGGED scan is unaffected, and that is the case which will
 * actually come up: `PATCH /attendance/events/:id/status` is core attendance,
 * carries no feature gate, and is surfaced on the employee's detail page.
 *
 * ── A GAP THIS PRESET INHERITS, STATED RATHER THAN HIDDEN ───────────────────
 * There is no screen anywhere in the console that CREATES a missing punch for
 * another employee. `POST /api/attendance/manual` exists and nothing calls it.
 * That is not caused by this preset — no combination of flags produces one —
 * but a terminal-only deployment feels it hardest, because somebody who walks
 * past the gate without scanning has no second capture path: no app, no
 * self-service, no field device. Until that screen exists such a day is fixed
 * through the API, or not at all.
 *
 * `faceRecognition` is off because it is HADIR's own face system, used by the
 * web punch and the field app. The terminal matches faces on the device, in
 * its own proprietary format, and never consults it.
 */
export const TERMINAL_ONLY_FEATURES: FeatureFlags = {
  leave: false,
  // The request/approve queue, and it raises for the CALLER's own record —
  // useless where nobody signs in. See the note above on what it does not cost.
  corrections: false,
  announcements: false,
  letters: false,
  payroll: false,
  // Reading the log IS the product here. Everything else can go; this cannot.
  reports: true,
  shifts: false,
  team: false,
  orgStructure: false,
  documents: false,
  // Hadir's own face system, not the terminal's. See above.
  faceRecognition: false,
  gratuity: false,
  lifecycle: false,
  dailyRoster: false,
  // Nobody signs in.
  selfService: false,
  // Load-bearing despite the name — see above.
  workers: true,
  camps: false,
  // The entire point.
  hardwareTerminals: true,
};
